import { Add, Info, Warning } from '@mui/icons-material';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAllEntities, useRecentEntities, useRootEntities } from 'api/entity';
import { useCreateEntityModal } from 'components/createEntity';
import EntityView from 'components/entityView';
import { ProjectsList } from 'components/ProjectsList';
import { plural } from 'pluralize';
import React, { useMemo, useState } from 'react';
import { formatName } from 'utils/fields';

export default function RootEntityTypePage(props: { type: string }) {
  const { type } = props;

  const recentProjects = useRecentEntities(type);
  const { showCreateEntityModal } = useCreateEntityModal();
  const [filter, setFilter] = useState<'root' | 'all'>('root');

  const name = useMemo(() => formatName(type), [type]);
  const namePlural = useMemo(() => plural(name), [name]);

  return (
    <Stack gap={1} pt={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          startIcon={<Add />}
          variant="outlined"
          onClick={() => showCreateEntityModal(type)}
        >
          New {name}
        </Button>
      </Stack>
      <Typography variant="h6">Recent {namePlural}</Typography>
      <Stack direction="row" gap={2} minWidth={0} overflow="auto">
        {recentProjects.data?.map((project) => (
          <EntityView entity={project} key={project.id} viewType="card" />
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">{namePlural}</Typography>
        <TextField
          select
          variant="standard"
          InputProps={{
            startAdornment: (
              <Typography variant="body2" pr={0.5}>
                Filter:
              </Typography>
            ),
          }}
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
        >
          <MenuItem value="root">Root</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
      </Stack>

      {filter === 'root' && (
        <RootEntityAlert
          type={type}
          entityName={namePlural}
          onViewAll={() => setFilter('all')}
        />
      )}

      {filter === 'root' ? (
        <RootEntities type={type} />
      ) : (
        <AllEntities type={type} />
      )}
    </Stack>
  );
}

function RootEntities(props: { type: string }) {
  const entities = useRootEntities(props.type);

  if (!entities.data?.length) {
    return null;
  }

  return <ProjectsList entities={entities.data} />;
}

function AllEntities(props: { type: string }) {
  const entities = useAllEntities(props.type);

  if (!entities.data?.length) {
    return null;
  }

  return <ProjectsList entities={entities.data} />;
}

function RootEntityAlert(props: {
  type: string;
  entityName: string;
  onViewAll: () => void;
}) {
  const entities = useRootEntities(props.type);

  // If we're still loading, don't show any alert
  if (entities.isLoading) {
    return null;
  }

  // Check if there are no entities
  const hasNoEntities = !entities.data?.length;

  // If there are no root entities, show warning alert
  if (hasNoEntities) {
    return (
      <Alert
        severity="warning"
        icon={<Warning />}
        action={
          <Button color="inherit" size="small" onClick={props.onViewAll}>
            View All
          </Button>
        }
      >
        {`No ${props.entityName.toLowerCase()} exist at the root level. Click "View All" to see all ${props.entityName.toLowerCase()}.`}
      </Alert>
    );
  }

  // Otherwise show the normal info alert
  return (
    <Alert
      severity="info"
      icon={<Info />}
      action={
        <Button color="inherit" size="small" onClick={props.onViewAll}>
          Show All
        </Button>
      }
    >
      {`Showing only the top-level ${props.entityName.toLowerCase()}.`}
    </Alert>
  );
}
